import React, { useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Loader2, Search, X } from "lucide-react";

/**
 * Searchable university dropdown.
 *
 * Props:
 *   universities – array of { name: string, acronym: string | null }
 *   loading      – show a loading skeleton while data fetches
 *   value        – currently selected university name (string)
 *   onChange     – (name: string) => void
 *   error        – truthy to show error border
 *   placeholder  – optional override for the empty-state label
 */
export default function UniversitySelect({
  universities = [],
  loading = false,
  value = "",
  onChange,
  error,
  placeholder = "Select your university or polytechnic",
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return universities;
    return universities.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        (u.acronym && u.acronym.toLowerCase().includes(q))
    );
  }, [query, universities]);

  const handleSelect = (uni) => {
    onChange(uni.name);
    setQuery("");
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setQuery("");
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // Close on outside click
  React.useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (loading) {
    return (
      <div
        className="form-input"
        style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--color-text-tertiary)", cursor: "default" }}
      >
        <Loader2 size={15} style={{ animation: "spin 1s linear infinite", flexShrink: 0 }} />
        <span style={{ fontSize: "var(--text-sm)" }}>Loading universities…</span>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); setTimeout(() => inputRef.current?.focus(), 0); }}
        className={`form-input${error ? " input-error" : ""}`}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", textAlign: "left", cursor: "pointer", gap: 8, width: "100%" }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={{ color: value ? "var(--color-text-primary)" : "var(--color-text-tertiary)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value || placeholder}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          {value && (
            <span
              role="button"
              onClick={handleClear}
              style={{ display: "flex", alignItems: "center", color: "var(--color-text-tertiary)", padding: 2 }}
              aria-label="Clear selection"
            >
              <X size={14} />
            </span>
          )}
          <ChevronDown
            size={16}
            style={{ color: "var(--color-text-tertiary)", transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}
          />
        </div>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 999, borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.22)", border: "1px solid var(--color-border)" }}
          >
            {/* Search input */}
            <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--color-border)", display: "flex", alignItems: "center", gap: 8, background: "var(--color-bg-secondary)", borderRadius: "12px 12px 0 0" }}>
              <Search size={15} style={{ color: "var(--color-text-tertiary)", flexShrink: 0 }} />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search university or acronym…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{ border: "none", outline: "none", background: "transparent", fontSize: 14, color: "var(--color-text-primary)", width: "100%" }}
              />
            </div>

            {/* Options list */}
            <ul
              role="listbox"
              style={{ maxHeight: 240, overflowY: "auto", margin: 0, padding: 6, listStyle: "none", background: "var(--color-bg-primary)", borderRadius: "0 0 12px 12px" }}
            >
              {filtered.length === 0 ? (
                <li style={{ padding: 12, textAlign: "center", color: "var(--color-text-tertiary)", fontSize: 14 }}>
                  No results for "{query}"
                </li>
              ) : (
                filtered.map((uni) => {
                  const isSelected = value === uni.name;
                  return (
                    <li
                      key={uni.name}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => handleSelect(uni)}
                      style={{
                        padding: "10px 12px",
                        borderRadius: 8,
                        cursor: "pointer",
                        fontSize: 14,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        background: isSelected ? "var(--color-primary-soft)" : "transparent",
                        color: isSelected ? "var(--color-primary)" : "var(--color-text-primary)",
                        fontWeight: isSelected ? 600 : 400,
                      }}
                      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "var(--color-bg-secondary)"; }}
                      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                    >
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{uni.name}</span>
                      {uni.acronym && (
                        <span style={{ fontSize: 11, color: isSelected ? "var(--color-primary)" : "var(--color-text-tertiary)", flexShrink: 0, fontWeight: 600 }}>
                          {uni.acronym}
                        </span>
                      )}
                    </li>
                  );
                })
              )}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
